import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

export type CalendarProvider = 'none' | 'google';

@Injectable({
  providedIn: 'root'
})
export class CalendarService {
  private provider: CalendarProvider = 'none';

  setProvider(provider: CalendarProvider): void {
    this.provider = provider;
  }

  getProvider(): CalendarProvider {
    return this.provider;
  }

  syncRoadmap(roadmapItems: any[]): Observable<{ success: boolean; message: string }> {
    const events = (roadmapItems || [])
      .filter((item: any) => !!item && !!item.deadline)
      .map((item: any) => ({
        title: item.title || 'Tangram study block',
        description: item.description || '',
        start: new Date(item.deadline),
        durationHours: Math.max(1, Math.round(Number(item.recommendedHours || item.estimatedHours || 1)))
      }))
      .filter((event: any) => !isNaN(event.start.getTime()));

    if (!events.length) {
      return of({ success: false, message: 'No roadmap deadlines available to sync.' });
    }

    if (this.provider === 'google') {
      const firstEvent = events[0];
      const end = new Date(firstEvent.start.getTime() + firstEvent.durationHours * 60 * 60 * 1000);
      const dates = `${this.formatGoogleDate(firstEvent.start)}/${this.formatGoogleDate(end)}`;
      const text = encodeURIComponent(firstEvent.title);
      const details = encodeURIComponent(firstEvent.description || 'Generated from Tangram AI Study Assistant roadmap.');
      const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&details=${details}&dates=${dates}`;
      window.open(url, '_blank');
      if (events.length > 1) {
        this.downloadIcs(events);
        return of({ success: true, message: 'Opened first event in Google Calendar and downloaded full roadmap as ICS.' });
      }
      return of({ success: true, message: 'Opened roadmap event in Google Calendar.' });
    }

    this.downloadIcs(events);
    return of({ success: true, message: 'Roadmap exported as calendar file (.ics).' });
  }

  private formatGoogleDate(date: Date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hour = String(date.getUTCHours()).padStart(2, '0');
    const minute = String(date.getUTCMinutes()).padStart(2, '0');
    const second = String(date.getUTCSeconds()).padStart(2, '0');
    return `${year}${month}${day}T${hour}${minute}${second}Z`;
  }

  private downloadIcs(events: Array<{ title: string; description: string; start: Date; durationHours: number }>): void {
    const now = this.formatGoogleDate(new Date());
    const lines: string[] = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Tangram//Learning Roadmap//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH'
    ];

    events.forEach((event, index) => {
      const end = new Date(event.start.getTime() + event.durationHours * 60 * 60 * 1000);
      lines.push('BEGIN:VEVENT');
      lines.push(`UID:tangram-roadmap-${index}-${now}`);
      lines.push(`DTSTAMP:${now}`);
      lines.push(`DTSTART:${this.formatGoogleDate(event.start)}`);
      lines.push(`DTEND:${this.formatGoogleDate(end)}`);
      lines.push(`SUMMARY:${this.escapeIcs(event.title)}`);
      lines.push(`DESCRIPTION:${this.escapeIcs(event.description || 'Generated from Tangram AI Study Assistant roadmap.')}`);
      lines.push('END:VEVENT');
    });

    lines.push('END:VCALENDAR');
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'tangram-learning-roadmap.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private escapeIcs(value: string): string {
    return String(value || '')
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r?\n/g, '\\n');
  }
}
